import React from 'react';
import { useFormContext } from '@alfa-retail-react/components/form-hook';
import SelectSearch from '@alfa-retail-react/components/select-search';
import Select from '@alfa-retail-react/components/select';
import Input from '@alfa-retail-react/components/input';
import { observer } from 'mobx-react';
import { Controller, Path } from 'react-hook-form';

import { WEB_VISOR_DISABLE, WEB_VISOR_HIDE } from 'app/utils/AnalyticsUtils';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { FieldSet } from 'app/component/fieldset/FieldSet';
import { AddressFormStore } from 'app/logic/address/AddressFormStore';
import { ReferenceService } from 'app/service/ReferenceService';
import { StreetReference } from 'app/model/StreetReference';
import { LocalityReference } from 'app/model/LocalityReference';
import {
    LocalityReferenceRenderer,
    PrimaryContent,
} from 'app/logic/address/LocalityReferenceRenderer';
import { StreetReferenceRenderer } from 'app/logic/address/StreetReferenceRenderer';
import { useIsDesktop } from 'app/utils/useWindowType';
import { ValidationRules } from 'app/form/ValidationRules';

import 'app/logic/address/AddressForm.less';

export type AddressFormProps<T> = {
    className?: string;

    /**
     * Хранилище данных формы ввода адреса
     */
    formStore: AddressFormStore;

    /**
     * Префикс для именования компонентов формы.
     * Указывает в каком контексте используется форма.
     */
    prefix: Path<T>;
};

export type AddressFormValues = {
    countryCode: string;
    locality: string;

    streetTypeCode: string;
    street: string;

    house: string;
    building: string;
    flat: string;

    postalCode: string;
};

/**
 * Форма ввода адреса
 */
export const AddressForm = observer(
    <T, TT>({ className = '', formStore, prefix }: AddressFormProps<T>) => {
        const ALLOWED_CHARACTERS_SET_1_REGEXP =
            /^[a-zA-Zа-яА-ЯёЁ0-9\-\\\/., ]+$/;
        const SET_1_VALIDATION_ERROR_MESSAGE =
            'Допустимые символы: буквы кириллицей (А, б, в), латиницей (A, b, с), цифры, дефис, слеш, точка, запятая, пробел';

        //todo: вызывает превентивний ререндер формы для изменения доступности полей.
        //mobx не отслеживает обращение к observable полям внутре render функций, используемых react hook form
        //
        //Необходимо использовать useController вместо Controller.
        formStore.hasLocalityAddress;
        formStore.hasStreetAddress;
        formStore.postIndexes;

        const {
            control,
            handleMultipleErrors,
            setValue,
            trigger,
            setFieldElement,
        } = useFormContext<T>();
        const fieldHandler = new InputFieldEventHandler<T>(trigger);

        const streetReferenceRenderer = (value: StreetReference) => (
            <div>
                <StreetReferenceRenderer
                    streetReference={value}
                    streetTypeConverter={(typeId) =>
                        formStore.getStreetTypeReferenceById(typeId)
                    }
                />
            </div>
        );

        const [isDesktop] = useIsDesktop();

        function clearStreetAddress() {
            formStore.setStreetAddress(null);
            setValue(`${String(prefix)}.street` as Path<T>, null);
        }

        function clearPostalCode() {
            formStore.setPostalCode(null);
            setValue(`${String(prefix)}.postalCode` as Path<T>, null);
        }

        return (
            <FieldSet
                className={`addressForm ${className}`}
                fieldsProps={[
                    {
                        className: 'countryCode',
                        title: 'Страна',
                        value: (
                            <span className={WEB_VISOR_HIDE}>
                                {formStore.country.name}
                            </span>
                        ),
                    },
                    {
                        className: 'locality',
                        title: '',
                        value: (
                            <Controller
                                //todo: Уточнить тип
                                control={control as any}
                                defaultValue={formStore.localityReference}
                                name={`${prefix.toString()}.locality`}
                                rules={{ required: ValidationRules.required() }}
                                render={({ field, fieldState }) => {
                                    return (
                                        <SelectSearch<LocalityReference>
                                            className={WEB_VISOR_HIDE}
                                            componentRef={(element) => {
                                                field.ref(element);
                                                setFieldElement(
                                                    `${prefix.toString()}.locality` as Path<T>,
                                                    element,
                                                );
                                            }}
                                            value={field.value}
                                            isDesktop={isDesktop}
                                            optionKey={(value) => value.id}
                                            selectedOptionTooltipRenderer={(
                                                value,
                                            ) => (
                                                <div>
                                                    <PrimaryContent
                                                        localityTypeStore={
                                                            formStore.localityTypeStore
                                                        }
                                                        localityReference={
                                                            value
                                                        }
                                                    />
                                                </div>
                                            )}
                                            selectedOptionRenderer={(value) => (
                                                <div>
                                                    <LocalityReferenceRenderer
                                                        localityTypeStore={
                                                            formStore.localityTypeStore
                                                        }
                                                        localityReference={
                                                            value
                                                        }
                                                    />
                                                </div>
                                            )}
                                            optionRenderer={(value) => {
                                                return (
                                                    <LocalityReferenceRenderer
                                                        localityTypeStore={
                                                            formStore.localityTypeStore
                                                        }
                                                        localityReference={
                                                            value
                                                        }
                                                    />
                                                );
                                            }}
                                            filter={(value) =>
                                                new ReferenceService().getLocalitiesReference(
                                                    value,
                                                )
                                            }
                                            updateData={(value) =>
                                                new ReferenceService().getLocalitiesReference(
                                                    value,
                                                )
                                            }
                                            error={handleMultipleErrors(
                                                fieldState.error,
                                            )}
                                            onBlur={(value) => {
                                                field.onChange(value);
                                                field.onBlur();
                                            }}
                                            label={'Населённый пункт'}
                                            onChange={(locality) => {
                                                field.onChange(locality);
                                                formStore.setLocalityAddress(
                                                    locality,
                                                );

                                                clearStreetAddress();
                                                clearPostalCode();
                                            }}
                                        />
                                    );
                                }}
                            />
                        ),
                    },
                    {
                        className: 'street',
                        title: '',
                        value: (
                            <Controller
                                control={control as any}
                                defaultValue={formStore.streetReference}
                                name={`${prefix.toString()}.street`}
                                rules={{ required: ValidationRules.required() }}
                                render={({ field, fieldState }) => {
                                    return (
                                        <SelectSearch<StreetReference>
                                            className={WEB_VISOR_HIDE}
                                            numberOfCharactersToStartFilter={1}
                                            componentRef={(element) => {
                                                field.ref(element);
                                                setFieldElement(
                                                    `${prefix.toString()}.street` as Path<T>,
                                                    element,
                                                );
                                            }}
                                            value={field.value}
                                            isDesktop={isDesktop}
                                            disabled={
                                                !formStore.hasLocalityAddress
                                            }
                                            optionKey={(value) => value.id}
                                            selectedOptionTooltipRenderer={
                                                streetReferenceRenderer
                                            }
                                            selectedOptionRenderer={
                                                streetReferenceRenderer
                                            }
                                            optionRenderer={
                                                streetReferenceRenderer
                                            }
                                            filter={(value) =>
                                                new ReferenceService().getStreetsReference(
                                                    value,
                                                    formStore.localityReference
                                                        .soato,
                                                )
                                            }
                                            updateData={(value) =>
                                                new ReferenceService().getStreetsReference(
                                                    value,
                                                    formStore.localityReference
                                                        .soato,
                                                )
                                            }
                                            error={handleMultipleErrors(
                                                fieldState.error,
                                            )}
                                            onBlur={(value) => {
                                                field.onChange(value);
                                                field.onBlur();
                                            }}
                                            label={'Улица'}
                                            onChange={(reference) => {
                                                field.onChange(reference);
                                                formStore.setStreetAddress(
                                                    reference,
                                                );
                                                clearPostalCode();
                                            }}
                                        />
                                    );
                                }}
                            />
                        ),
                    },
                    {
                        className: 'house',
                        title: '',
                        value: (
                            <>
                                <Controller
                                    control={control as any}
                                    defaultValue={formStore.house ?? ''}
                                    name={`${prefix.toString()}.house`}
                                    rules={{
                                        required: ValidationRules.required(),
                                        maxLength: ValidationRules.maxLength(7),
                                        pattern:
                                            ValidationRules.allowedCharacters(
                                                ALLOWED_CHARACTERS_SET_1_REGEXP,
                                                SET_1_VALIDATION_ERROR_MESSAGE,
                                            ),
                                    }}
                                    render={({ field, fieldState }) => {
                                        // @ts-ignore инстанс AddressForm может иметь разные типы FieldValues, что влияет на типизацию field
                                        const { onBlur, onClear, onChange } =
                                            fieldHandler.getEventHandlers(
                                                field,
                                                `${prefix.toString()}.house`,
                                                formStore.setHouse,
                                            );

                                        return (
                                            <Input
                                                clear
                                                shouldUpperCase
                                                inputClassName={
                                                    WEB_VISOR_DISABLE
                                                }
                                                ref={(element) => {
                                                    field.ref(element);
                                                    setFieldElement(
                                                        `${prefix.toString()}.house` as Path<T>,
                                                        element,
                                                    );
                                                }}
                                                value={field.value}
                                                size="l"
                                                error={handleMultipleErrors(
                                                    fieldState.error,
                                                )}
                                                label="Дом"
                                                onBlur={onBlur}
                                                onClear={onClear}
                                                onChange={onChange}
                                            />
                                        );
                                    }}
                                />
                                <Controller
                                    control={control as any}
                                    defaultValue={formStore.building ?? ''}
                                    name={`${prefix.toString()}.building`}
                                    rules={{
                                        maxLength: ValidationRules.maxLength(3),
                                        pattern:
                                            ValidationRules.allowedCharacters(
                                                ALLOWED_CHARACTERS_SET_1_REGEXP,
                                                SET_1_VALIDATION_ERROR_MESSAGE,
                                            ),
                                    }}
                                    render={({ field, fieldState }) => {
                                        // @ts-ignore инстанс AddressForm может иметь разные типы FieldValues, что влияет на типизацию field
                                        const { onBlur, onClear, onChange } =
                                            fieldHandler.getEventHandlers(
                                                field,
                                                `${prefix.toString()}.building`,
                                                formStore.setBuilding,
                                            );

                                        return (
                                            <Input
                                                clear
                                                inputClassName={
                                                    WEB_VISOR_DISABLE
                                                }
                                                ref={(element) => {
                                                    field.ref(element);
                                                    setFieldElement(
                                                        `${prefix.toString()}.building` as Path<T>,
                                                        element,
                                                    );
                                                }}
                                                value={field.value}
                                                size={'l'}
                                                error={handleMultipleErrors(
                                                    fieldState.error,
                                                )}
                                                label={'Корпус'}
                                                shouldUpperCase={true}
                                                onBlur={onBlur}
                                                onClear={onClear}
                                                onChange={onChange}
                                            />
                                        );
                                    }}
                                />
                                <Controller
                                    control={control as any}
                                    defaultValue={formStore.flat ?? ''}
                                    name={`${prefix.toString()}.flat`}
                                    rules={{
                                        maxLength: ValidationRules.maxLength(7),
                                        pattern:
                                            ValidationRules.allowedCharacters(
                                                ALLOWED_CHARACTERS_SET_1_REGEXP,
                                                SET_1_VALIDATION_ERROR_MESSAGE,
                                            ),
                                    }}
                                    render={({ field, fieldState }) => {
                                        // @ts-ignore инстанс AddressForm может иметь разные типы FieldValues, что влияет на типизацию field
                                        const { onBlur, onClear, onChange } =
                                            fieldHandler.getEventHandlers(
                                                field,
                                                `${prefix.toString()}.flat`,
                                                formStore.setFlat,
                                            );

                                        return (
                                            <Input
                                                clear
                                                inputClassName={
                                                    WEB_VISOR_DISABLE
                                                }
                                                ref={(element) => {
                                                    field.ref(element);
                                                    setFieldElement(
                                                        `${prefix.toString()}.flat` as Path<T>,
                                                        element,
                                                    );
                                                }}
                                                value={field.value}
                                                size={'l'}
                                                error={handleMultipleErrors(
                                                    fieldState.error,
                                                )}
                                                label={'Квартира'}
                                                shouldUpperCase={true}
                                                onBlur={onBlur}
                                                onClear={onClear}
                                                onChange={onChange}
                                            />
                                        );
                                    }}
                                />
                            </>
                        ),
                    },
                    {
                        className: 'postalCode',
                        title: '',
                        value: (
                            <Controller
                                control={control as any}
                                name={`${prefix.toString()}.postalCode`}
                                defaultValue={formStore.postalCode}
                                rules={{ required: ValidationRules.required() }}
                                render={({ field, fieldState }) => {
                                    return (
                                        <Select
                                            className={WEB_VISOR_HIDE}
                                            componentRef={(element) => {
                                                field.ref(element);
                                                setFieldElement(
                                                    `${prefix.toString()}.postalCode` as Path<T>,
                                                    element,
                                                );
                                            }}
                                            isDesktop={isDesktop}
                                            size={'l'}
                                            selected={field.value}
                                            options={formStore.postIndexes}
                                            disabled={
                                                !formStore.hasStreetAddress
                                            }
                                            error={handleMultipleErrors(
                                                fieldState.error,
                                            )}
                                            onBlur={field.onBlur}
                                            label={'Индекс'}
                                            onChange={(event) => {
                                                field.onChange(
                                                    event.selected.key,
                                                );
                                                formStore.setPostalCode(
                                                    event.selected.key,
                                                );
                                            }}
                                        />
                                    );
                                }}
                            />
                        ),
                    },
                ]}
            />
        );
    },
);
